import type { Edge, Node } from '@xyflow/react';
import type { z } from 'zod';

import type { CreateTransitionFunctionPayload } from '@polyai/stream/commands/handlers/flow/create-transition-function/schema';
import type {
  FlowStepPositionDetailSchemaDiscriminated,
  StepReferences,
} from '@polyai/stream/commands/handlers/flow/schema';
import {
  flowFunctionsSelectors,
  selectAdvancedStepById,
  selectAdvancedSteps,
  selectDefaultStepById,
  selectDefaultSteps,
  selectFunctionSteps,
  stepsSelectors,
} from '@polyai/stream/store/reducers/flows';
import type {
  AdvancedStep,
  DefaultStep,
  Flow,
  FunctionStep,
  StepAsrBiasing,
  StepDTMFConfig,
  StepPosition,
} from '@polyai/stream/store/reducers/flows/types';
import { isFunctionStep } from '@polyai/stream/store/reducers/flows/types';
import {
  getDefaultAsrBiasing,
  getDefaultDTMFConfig,
} from '@polyai/stream/store/reducers/flows/utils';

import {
  ALL_ACTION_PREFIXES,
  FLOW_TRANSITION_PREFIX,
  FUNCTION_PREFIX,
  HANDOFF_PREFIX,
  TWILIO_SMS_PREFIX,
  VARIANT_ATTRIBUTE_PREFIX,
} from 'components/molecules/RichTextInput/constants';
import { generateDefaultFunctionCode } from 'components/molecules/RichTextInput/utils';
import {
  DRAG_HANDLE_CLASS,
  EDGE_CONFIG_MARKER,
  NodeType,
  SHARED_EDGE_CONFIG,
} from 'screens/FlowDetails/constants';

export type NodeDataReferences = {
  sms: Record<string, boolean>;
  globalFunctions: Record<string, boolean>;
  transitionFunctions: Record<string, boolean>;
  handoff: Record<string, boolean>;
  attributes: Record<string, boolean>;
  entities: Record<string, boolean>;
  variables: Record<string, boolean>;
};
export interface AdvancedNodeData extends Record<string, unknown> {
  name: string;
  isStartStep: boolean;
  prompt: string;
  asrBiasing: StepAsrBiasing;
  dtmfConfig: StepDTMFConfig;
  references: NodeDataReferences;
}

export interface NoCodeNodeData extends Record<string, unknown> {
  name: string;
  prompt: string;
  isStartStep: boolean;
}

export interface FunctionStepNodeData extends Record<string, unknown> {
  stepId: string;
  name: string;
  // TODO: add function code properties
}

export interface ConditionNodeData extends Record<string, unknown> {
  stepId: string;
  conditionId: string;
  name: string;
}

export interface ExitFlowNodeData extends Record<string, unknown> {
  stepId: string;
  conditionId: string;
  name: string;
}

// Temporary node for selecting next step. This node is not persisted in back-end.
export interface NextStepSelectionNodeData extends Record<string, unknown> {
  sourceNodeId: string;
}

export type ReactFlowNode =
  | Node<AdvancedNodeData, NodeType.STEP_NODE>
  | Node<NoCodeNodeData, NodeType.NO_CODE_STEP_NODE>
  | Node<FunctionStepNodeData, NodeType.FUNCTION_STEP_NODE>
  | Node<ConditionNodeData, NodeType.CONDITION_NODE>
  | Node<ExitFlowNodeData, NodeType.EXIT_FLOW_NODE>
  | Node<NextStepSelectionNodeData, NodeType.NEXT_STEP_SELECTION_NODE>
  | Node<ConditionNodeData, NodeType.FUNCTION_STEP_CONDITION_NODE>;

export const getAdvancedNode = (
  {
    id,
    name,
    prompt = '',
    position,
    asrBiasing,
    dtmfConfig,
    references,
  }: Omit<
    Omit<AdvancedStep, 'type'>,
    | 'asrBiasing'
    | 'dtmfConfig'
    | 'createdAt'
    | 'createdBy'
    | 'updatedAt'
    | 'updatedBy'
  > & {
    asrBiasing?: StepAsrBiasing;
    dtmfConfig?: StepDTMFConfig;
  },
  startStepId: string = '',
): Node<AdvancedNodeData, NodeType.STEP_NODE> => {
  return {
    id,
    type: NodeType.STEP_NODE,
    position: toSafePosition(position),
    dragHandle: `.${DRAG_HANDLE_CLASS}`,
    data: {
      name,
      prompt,
      asrBiasing: asrBiasing ?? getDefaultAsrBiasing(),
      dtmfConfig: dtmfConfig ?? getDefaultDTMFConfig(),
      isStartStep: id === startStepId,
      references: references,
    },
  };
};

export const getDefaultNode = (
  { id, name, prompt = '', position }: DefaultStep,
  startStepId: string = '',
): Node<NoCodeNodeData, NodeType.NO_CODE_STEP_NODE> => {
  return {
    id,
    type: NodeType.NO_CODE_STEP_NODE,
    position: toSafePosition(position),
    dragHandle: `.${DRAG_HANDLE_CLASS}`,
    connectable: true,
    data: {
      name,
      prompt,
      isStartStep: id === startStepId,
    },
  };
};

export const getFunctionStepNode = ({
  id,
  name,
  position,
}: FunctionStep): Node<FunctionStepNodeData, NodeType.FUNCTION_STEP_NODE> => {
  return {
    id,
    type: NodeType.FUNCTION_STEP_NODE,
    position: toSafePosition(position),
    dragHandle: `.${DRAG_HANDLE_CLASS}`,
    data: {
      name,
      stepId: id,
    },
  };
};

export const getConditionNode = ({
  id,
  stepId,
  name,
  position,
}: {
  id: string;
  stepId: string;
  name: string;
  position: StepPosition;
}): Node<ConditionNodeData, NodeType.CONDITION_NODE> => {
  return {
    id,
    type: NodeType.CONDITION_NODE,
    position: toSafePosition(position),
    dragHandle: `.${DRAG_HANDLE_CLASS}`,
    data: {
      name,
      stepId,
      conditionId: id,
    },
  };
};

export const getExitFlowNodeId = (conditionId: string) => `${conditionId}-exit`;

export const getExitFlowNode = ({
  id,
  stepId,
  conditionId,
  name,
  position,
}: {
  id: string;
  stepId: string;
  conditionId: string;
  name: string;
  position: StepPosition;
}): Node<ExitFlowNodeData, NodeType.EXIT_FLOW_NODE> => {
  return {
    id,
    type: NodeType.EXIT_FLOW_NODE,
    position: toSafePosition(position),
    dragHandle: `.${DRAG_HANDLE_CLASS}`,
    data: {
      name,
      stepId,
      conditionId,
    },
  };
};

const toSafePosition = (position: StepPosition) => ({
  x: isNaN(position.x) ? 0 : position.x,
  y: isNaN(position.y) ? 0 : position.y,
});

export const getNodes = (data: Flow): ReactFlowNode[] => {
  if (!data?.steps) {
    return [];
  }

  const { startStepId } = data;

  // Use the new selectors to get steps by type
  const advancedSteps = selectAdvancedSteps(data);
  const defaultSteps = selectDefaultSteps(data);
  const functionSteps = selectFunctionSteps(data);

  const advancedNodes = advancedSteps.map(s => getAdvancedNode(s, startStepId));
  const defaultNodes = defaultSteps.map(s => getDefaultNode(s, startStepId));
  const functionNodes = functionSteps.map(s => getFunctionStepNode(s));

  // Add conditional nodes from default steps conditions
  const conditionNodes: ReactFlowNode[] = [];
  const exitFlowNodes: ReactFlowNode[] = [];

  defaultSteps.forEach(defaultStep => {
    defaultStep.conditions.forEach(condition => {
      if (condition.config) {
        const conditionNode = getConditionNode({
          id: condition.id,
          stepId: defaultStep.id,
          name: condition.config.value.details.label,
          position: condition.config.value.details.position,
        });
        conditionNodes.push(conditionNode);
        if (condition.config.$case === 'exitFlowCondition') {
          const exitFlowNode = getExitFlowNode({
            id: getExitFlowNodeId(condition.id),
            stepId: defaultStep.id,
            conditionId: condition.id,
            name: 'Exit Flow',
            position: {
              x: condition.config.value.exitFlowPosition.x,
              y: condition.config.value.exitFlowPosition.y,
            },
          });
          exitFlowNodes.push(exitFlowNode);
        }
      }
    });
  });

  // Add conditional nodes from function steps conditions
  functionSteps.forEach(functionStep => {
    functionStep.conditions.forEach(condition => {
      if (condition.config) {
        // Skip condition nodes for exit flow conditions from function steps
        // We connect directly from function step to exit flow node
        if (condition.config.$case === 'exitFlowCondition') {
          const exitFlowNode = getExitFlowNode({
            id: getExitFlowNodeId(condition.id),
            stepId: functionStep.id,
            conditionId: condition.id,
            name: 'Exit Flow',
            position: {
              x: condition.config.value.exitFlowPosition.x,
              y: condition.config.value.exitFlowPosition.y,
            },
          });
          exitFlowNodes.push(exitFlowNode);
        } else {
          // For non-exit-flow conditions, create condition nodes
          const conditionNode = getConditionNode({
            id: condition.id,
            stepId: functionStep.id,
            name: condition.config.value.details.label,
            position: condition.config.value.details.position,
          });
          conditionNodes.push(conditionNode);
        }
      }
    });
  });

  return [
    ...advancedNodes,
    ...defaultNodes,
    ...conditionNodes,
    ...exitFlowNodes,
    ...functionNodes,
  ];
};

export const getEdges = (data: Flow): Edge[] => {
  const { transitionFunctions } = data;

  const transitionsMap: { [key: string]: string[] } = {};

  // Use the new selectors to get steps by type
  const advancedSteps = selectAdvancedSteps(data);
  const defaultSteps = selectDefaultSteps(data);
  const functionSteps = selectFunctionSteps(data);

  // Build name-to-ID map for both advanced steps and default steps
  const stepNameIdMap = Object.fromEntries([
    ...advancedSteps.map(s => [s.name, s.id]),
    ...defaultSteps.map(s => [s.name, s.id]),
    ...functionSteps.map(s => [s.name, s.id]),
  ]);

  // Build transitions map
  advancedSteps.forEach(s => {
    const transitions = Object.keys(s.references?.transitionFunctions || {})
      // check function actually exists in list of flow functions
      .filter(funcId => {
        const func = flowFunctionsSelectors.selectById(
          transitionFunctions,
          funcId,
        );
        return !!func && !func.archived;
      })
      .map(funcId =>
        findTransitionsForStep(
          flowFunctionsSelectors.selectById(transitionFunctions, funcId)?.code,
        ),
      );

    const flattenedTransitions = transitions.reduce(
      (acc, curr) => [...acc, ...curr],
      [],
    );

    transitionsMap[s.id] = [...new Set(flattenedTransitions)];
  });

  const edges: Edge[] = [];

  // Build edges array for step transitions
  Object.entries(transitionsMap).forEach(([name, transitions]) => {
    if (transitions.length) {
      transitions.forEach(transition => {
        const targetId = stepNameIdMap[transition];
        if (!targetId) {
          return;
        }

        // Check if target is a no-code step, advanced step, or function step using the new selectors
        const targetNoCodeStep = selectDefaultStepById(data, targetId);
        const targetAdvancedStep = selectAdvancedStepById(data, targetId);
        const targetFunctionStep = data.steps.entities[targetId];

        if (targetNoCodeStep) {
          // Transition to no-code step - use top handle as default
          edges.push({
            ...SHARED_EDGE_CONFIG,
            markerEnd: EDGE_CONFIG_MARKER,
            id: `${name}-${transition}`,
            source: name,
            target: targetId,
            targetHandle: `${targetId}-top-handle`,
          });
        } else if (targetAdvancedStep) {
          // Transition to advanced step - no specific handle needed
          edges.push({
            ...SHARED_EDGE_CONFIG,
            markerEnd: EDGE_CONFIG_MARKER,
            id: `${name}-${transition}`,
            source: name,
            target: targetId,
          });
        } else if (targetFunctionStep && isFunctionStep(targetFunctionStep)) {
          // Transition to function step - no specific handle needed
          edges.push({
            ...SHARED_EDGE_CONFIG,
            markerEnd: EDGE_CONFIG_MARKER,
            id: `${name}-${transition}`,
            source: name,
            target: targetId,
          });
        }
      });
    }
  });

  // Build edges array for DefaultSteps, FunctionSteps and their conditions
  [...defaultSteps, ...functionSteps].forEach(step => {
    step.conditions.forEach(condition => {
      if (!condition.config) {
        return;
      }

      // Get ingress position from condition details, default to "top"
      const ingressPosition = (
        condition.config?.value.details.ingressPosition || 'top'
      ).toLowerCase();

      // Egress position is the opposite of ingress position
      const egressPosition = getOppositePosition(ingressPosition);

      // Edge from DefaultStep to each condition
      edges.push({
        ...SHARED_EDGE_CONFIG,
        id: `${step.id}-${condition.id}`,
        source: step.id,
        target: condition.id,
        targetHandle: `${condition.id}-${ingressPosition}-handle`,
      });

      // Edge from condition to its target based on condition type
      const conditionEdgeConfig = {
        ...SHARED_EDGE_CONFIG,
        source: condition.id,
        sourceHandle: `${condition.id}-${egressPosition}-handle`,
        markerEnd: EDGE_CONFIG_MARKER,
      };

      switch (condition.config.$case) {
        case 'stepCondition':
        case 'noCodeStepCondition':
        case 'functionStepCondition': {
          const childStepId = condition.config.value.childStepId;
          const edge: Edge = {
            ...conditionEdgeConfig,
            id: `${condition.id}-${childStepId}`,
            target: childStepId,
          };
          if (
            // Check if target step exists
            (condition.config.$case === 'stepCondition' &&
              selectAdvancedStepById(data, childStepId)) ||
            (condition.config.$case === 'noCodeStepCondition' &&
              selectDefaultStepById(data, childStepId)) ||
            (condition.config.$case === 'functionStepCondition' &&
              data.steps.entities[childStepId] &&
              isFunctionStep(data.steps.entities[childStepId]))
          ) {
            edges.push(edge);
          }
          break;
        }

        case 'exitFlowCondition': {
          const exitFlowNodeId = getExitFlowNodeId(condition.id);
          if (step.type === 'function_step') {
            // For exit flow conditions from function steps, connect directly from function step to exit flow node
            edges.push({
              ...SHARED_EDGE_CONFIG,
              markerEnd: EDGE_CONFIG_MARKER,
              id: `${step.id}-${exitFlowNodeId}`,
              source: step.id,
              target: exitFlowNodeId,
            });
          } else {
            // Create edge from condition to exit flow node
            edges.push({
              ...conditionEdgeConfig,
              id: `${condition.id}-${exitFlowNodeId}`,
              target: exitFlowNodeId,
            });
          }
          break;
        }
      }
    });
  });

  return edges;
};

export const functionIsExit = (code: string) =>
  code &&
  code.includes('conv.exit_flow()') &&
  !code.includes('flow.goto_step(');

// Matches flow.goto_step() with positional or keyword `step=` argument, with or without label
// Positional: flow.goto_step('step_name') or flow.goto_step('step_name', 'label') or flow.goto_step('step_name', label='label')
// Keyword: flow.goto_step(step='step_name', ...) or flow.goto_step(label='...', step='step_name')
const positionalStepRegex =
  /flow\.goto_step\(["']([^"']+)["'](?:\s*,\s*(?:label\s*=\s*)?["'][^"']*["'])?\)/g;
const keywordStepRegex =
  /flow\.goto_step\([^)]*step\s*=\s*["']([^"']+)["'][^)]*\)/g;

const findTransitionsForStep = (code: string) => {
  const positionalMatches = [...code.matchAll(positionalStepRegex)];
  const keywordMatches = [...code.matchAll(keywordStepRegex)];

  const transitions = [
    ...positionalMatches.map(m => m[1]),
    ...keywordMatches.map(m => m[1]),
  ];

  return [...new Set(transitions)];
};

export const getEntitiesReferenced = (prompt: string): StepReferences => {
  return [...prompt.matchAll(/{{(.*?)}}/g)].reduce(
    (acc, match) => {
      const content = match[1];
      const type = ALL_ACTION_PREFIXES.find(p => content.startsWith(p));

      if (!type) {
        return acc;
      }
      const id = content.replace(type, '');

      switch (type) {
        case FUNCTION_PREFIX:
          acc.globalFunctions[id] = true;
          break;
        case FLOW_TRANSITION_PREFIX:
          acc.transitionFunctions[id] = true;
          break;
        case TWILIO_SMS_PREFIX:
          acc.sms[id] = true;
          break;
        case HANDOFF_PREFIX:
          acc.handoff[id] = true;
          break;
        case VARIANT_ATTRIBUTE_PREFIX:
          acc.attributes[id] = true;
          break;
        default:
          break;
      }
      return acc;
    },
    {
      sms: {},
      handoff: {},
      attributes: {},
      transitionFunctions: {},
      globalFunctions: {},
    } as StepReferences,
  );
};

const defaultToStepName = 'enter_your_step_name';

export const getDefaultTransitionFunctionCode = (
  toStepName: string | undefined,
) => {
  return `${
    toStepName
      ? '# Make sure your Flow function either transitions to a step or exits the flow\n'
      : ''
  }  flow.goto_step("${toStepName || defaultToStepName}")`;
};

export const getDefaultTransitionFunctionDescription = (
  toStepName: string | undefined,
) => {
  return `Transition to step ${toStepName ?? defaultToStepName}`;
};

export const getDefaultTransition = ({
  id,
  transitionName,
  toStepName,
  fromStepId,
}: {
  id: string;
  transitionName: string;
  toStepName?: string;
  fromStepId?: string;
}) => {
  const newTransition: CreateTransitionFunctionPayload['transitionFunction'] = {
    id,
    name: transitionName,
    code: generateDefaultFunctionCode({
      functionName: transitionName,
      functionType: 'flow_function',
      code: getDefaultTransitionFunctionCode(toStepName),
    }),
    parameters: [],
    description: getDefaultTransitionFunctionDescription(toStepName),
    errors: [],
    latencyControl: {
      initialDelay: 0,
      interval: 0,
      delayResponses: [],
      enabled: false,
    },
    archived: false,
    references: {
      flowSteps: fromStepId
        ? {
            [fromStepId]: true,
          }
        : {},
      variables: {},
    },
  };

  return newTransition;
};

// Helper function to determine node type and create appropriate position detail
export const createPositionDetail = ({
  nodeId,
  node,
  newPosition = { x: 0, y: 0 },
  flow,
}: {
  nodeId: string;
  node: ReactFlowNode;
  newPosition: { x: number; y: number } | undefined;
  nodes?: ReactFlowNode[];
  flow?: Flow;
}): z.infer<typeof FlowStepPositionDetailSchemaDiscriminated> | null => {
  // Determine the node type based on the React Flow node type
  if (node.type === NodeType.STEP_NODE) {
    return {
      $case: 'flowStep' as const,
      value: {
        stepId: nodeId,
        newPosition,
      },
    };
  }
  if (node.type === NodeType.NO_CODE_STEP_NODE) {
    return {
      $case: 'noCodeStep' as const,
      value: {
        stepId: nodeId,
        newPosition,
      },
    };
  }
  if (node.type === NodeType.FUNCTION_STEP_NODE) {
    return {
      $case: 'functionStep' as const,
      value: {
        stepId: nodeId,
        newPosition,
      },
    };
  }
  // For condition nodes, get stepId and conditionId from node data
  const stepId = node.data.stepId as string;
  const conditionId = (node.data.conditionId as string) || nodeId; // Use nodeId as fallback for conditionId

  if (!stepId) {
    console.warn(`Could not find stepId in node data for condition ${nodeId}`);
    return null;
  }

  switch (node.type) {
    case NodeType.EXIT_FLOW_NODE: {
      // Check the parent step type by looking up the step in the flow
      const step = flow ? stepsSelectors.selectById(flow.steps, stepId) : null;
      const isFunctionStep = step?.type === 'function_step';

      if (isFunctionStep) {
        return {
          $case: 'functionStepConditionExitFlow' as const,
          value: {
            stepId,
            conditionId,
            newPosition,
          },
        };
      }

      // Default to noCodeStepConditionExitFlow for default steps
      return {
        $case: 'noCodeStepConditionExitFlow' as const,
        value: {
          stepId,
          conditionId,
          newPosition,
        },
      };
    }
    case NodeType.CONDITION_NODE: {
      // Check the parent step type by looking up the step in the flow
      const step = flow ? stepsSelectors.selectById(flow.steps, stepId) : null;
      const isFunctionStep = step?.type === 'function_step';

      if (isFunctionStep) {
        return {
          $case: 'functionStepCondition' as const,
          value: {
            stepId,
            conditionId,
            newPosition,
          },
        };
      }

      // Default to noCodeStepCondition for default steps
      return {
        $case: 'noCodeStepCondition' as const,
        value: {
          stepId,
          conditionId,
          newPosition,
        },
      };
    }
    case NodeType.FUNCTION_STEP_CONDITION_NODE:
      return {
        $case: 'functionStepCondition' as const,
        value: {
          stepId,
          conditionId,
          newPosition,
        },
      };

    default:
      return null;
  }
};

const getOppositePosition = (position: string): string => {
  switch (position) {
    case 'left':
      return 'right';
    case 'right':
      return 'left';
    case 'bottom':
      return 'top';
    case 'top':
    default:
      return 'bottom';
  }
};
